import {
  lazy,
  Suspense,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Input,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Skeleton,
  SkeletonItem,
  Spinner,
  Tab,
  TabList,
  useMergedRefs,
} from '@coreai/fluentui-react';
import { SearchRegular, ArrowClockwiseRegular, ArrowSortDownRegular, ArrowSortUpRegular, Flow16Regular, AddRegular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDeleteWorkflows } from '@/hooks/useDeleteWorkflows';
import { useNotificationStore } from '@/stores/notificationStore';
import { syncOnboardingFromData } from '@/hooks/useOnboardingProgress';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { EmptyState } from '@/components/shared/EmptyState';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import { OverflowMenu } from '@/components/shared/OverflowMenu';
import {
  EntityNameCell,
  DotStatusBadge,
  TablePaginationFooter,
  DataTablePanel,
  usePagination,
  type DotStatusKind,
} from '@/components/shared/DataTable';
import { WorkflowBuilderHero } from '@/components/WorkflowBuilderHero';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { InlineTemplateGallery } from '@/components/WorkflowDesigner/Templates/InlineTemplateGallery';
import { TemplateGalleryDialog } from '@/components/WorkflowDesigner/panels';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCreateWorkflowFromPromptEnabled } from '@/hooks/useWorkflowAssistantFeatures';
import { openCreateWorkflowDialog } from '@/stores/createWorkflowDialogStore';
import { useCreateWorkflow, generateUniqueName } from '@/hooks/useCreateWorkflow';
import { CREATE_FLAG_PARAM } from '@/routes/queryParams';
import { DeleteConfirmDialog } from '@/components/common/DeleteConfirmDialog';
import { useTelemetry } from '@/hooks/useTelemetry';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import { useAppPermission } from '@/hooks/useAppPermission';
import { formatDateTime } from '@/utils/dateTime';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { useEnvironmentSettings, useUpdateEnvironmentSettings } from '@/hooks/useEnvironmentSettings';
import { useWorkflowDraftStatuses, workflowDraftStatusesQueryPrefix } from '@/hooks/useWorkflowDraftStatuses';
import { workflowGraphDefinitionQueryPrefix } from '@/hooks/useWorkflowGraphDefinitions';
import { WorkflowUnpublishedBadge } from '@/components/WorkflowList/WorkflowUnpublishedBadge';
import { WorkflowCreateMenu } from '@/components/WorkflowList/WorkflowCreateMenu';
import { workflowListMessages } from '@/components/WorkflowList/workflowListMessages';
import { createWorkflowActionMenuItems } from '@/components/WorkflowList/workflowActionMenuItems';
import type { Workflow } from '@/types/logicApps';
import { APP_SHELL_MOBILE_QUERY } from '@/theme/appShell';

const LazyWorkflowDependencyGraph = lazy(() =>
  import('@/components/WorkflowList/WorkflowDependencyGraphView').then((module) => ({
    default: module.WorkflowDependencyGraph,
  })),
);

type TemplateSource = 'workflow-list-hero' | 'workflow-list-toolbar' | 'workflow-list-empty-state';

const mapWorkflowState = (state: string | undefined): DotStatusKind => {
  switch (state) {
    case 'Enabled':
      return 'Active';
    case 'Disabled':
      return 'Inactive';
    default:
      return 'Inactive';
  }
};

const useStyles = makeStyles({
  page: {
    // AppLayout's `content` wrapper already pads with XXXL — don't double up here.
    // Vertical rhythm is owned by each section (PageSection.marginTop = L),
    // not by a flex `gap` here — adding one stacks with PageSection's margin
    // and inflates the space below the AppOverviewHeader vs. other pages.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    // Constrain to the viewport so the table area below can own its own
    // scrollbar instead of the whole page scrolling under the sticky header.
    minHeight: 0,
    overflow: 'hidden',
  },
  // PageSection is content-sized by default. Inside `.page` (which is
  // `overflow: hidden`), we need to give the section a bounded flex slot
  // so the inner `DataTablePanel` can claim the remaining height and
  // pin its footer to the viewport bottom.
  tableSection: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  emptyPage: {
    overflowY: 'auto',
    rowGap: `calc(3 * ${tokens.spacingVerticalXXXL})`,
  },
  emptyActionRow: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalL,
  },
  emptyLibraryError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      width: '100%',
    },
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      width: '100%',
      flexWrap: 'wrap',
    },
  },
  search: {
    minWidth: '320px',
    [`@media ${APP_SHELL_MOBILE_QUERY}`]: {
      width: '100%',
      minWidth: 0,
    },
  },
  metadata: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  colName: { width: '65%' },
  colDateModified: { width: '15%' },
  colStatus: { width: '13%' },
  actionsCell: {
    width: '7%',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    paddingRight: tokens.spacingHorizontalM,
  },
  statusBadges: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  viewSwitcher: {
    flexShrink: 0,
  },
  graphLoading: {
    minHeight: '360px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  srOnly: visuallyHiddenStyle,
  skeletonHero: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  skeletonTextarea: {
    height: '56px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonSuggestionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  skeletonSuggestion: {
    width: '120px',
    height: '28px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonTableRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
});

/**
 * Content shimmer shown *inside* the page while workflows are loading.
 *
 * The page-level `AppOverviewHeader` stays mounted above this during load so
 * the page chrome doesn't disappear and reappear — matching the loading
 * behavior of `AppListPage`, `Analytics`, Connections, and Parameters.
 */
const WorkflowListSkeleton = ({ isAssistantEnabled }: { isAssistantEnabled: boolean }) => {
  const styles = useStyles();
  return (
    <div role="status" aria-label="Loading workflows">
      <Skeleton>
        {isAssistantEnabled && (
          <div className={styles.skeletonHero}>
            <SkeletonItem size={20} style={{ width: '260px' }} />
            <SkeletonItem size={12} style={{ width: '380px' }} />
            <SkeletonItem className={styles.skeletonTextarea} />
            <div className={styles.skeletonSuggestionRow}>
              <SkeletonItem size={12} style={{ width: '60px' }} />
              <SkeletonItem className={styles.skeletonSuggestion} />
              <SkeletonItem className={styles.skeletonSuggestion} />
              <SkeletonItem className={styles.skeletonSuggestion} />
            </div>
            <div className={styles.skeletonFooter}>
              <SkeletonItem size={16} style={{ width: '140px' }} />
              <SkeletonItem size={32} style={{ width: '200px', borderRadius: tokens.borderRadiusMedium }} />
            </div>
          </div>
        )}

        {/* Table header shimmer */}
        <div className={styles.skeletonHeader}>
          <SkeletonItem size={20} style={{ width: '100px' }} />
          <SkeletonItem size={28} style={{ width: '200px', borderRadius: tokens.borderRadiusMedium }} />
        </div>

        {/* Table rows shimmer */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.skeletonTableRow}>
            <SkeletonItem size={16} style={{ flex: 1 }} />
            <SkeletonItem size={16} style={{ width: '80px' }} />
            <SkeletonItem size={16} style={{ width: '48px' }} />
          </div>
        ))}
      </Skeleton>
    </div>
  );
};

export const WorkflowListPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { projectName, locationName } = useCurrentProject();
  const { appName, app } = useCurrentApp();
  useDocumentTitle(['Workflows', appName, projectName]);
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data: workflows, isLoading, error, isFetching } = useAppWorkflows(app ?? null);
  const isGraphEnabled = useFeatureFlag('enable-list-workflows-graph-view');
  const [view, setView] = useState<'list' | 'graph'>('list');
  const activeView = isGraphEnabled ? view : 'list';
  const [search, setSearch] = useState('');
  const refreshButtonRef = useRef<HTMLButtonElement>(null);
  const setRefreshButtonRef = useMergedRefs<HTMLButtonElement>(refreshButtonRef);
  // `?create=1` deep-links to the global CreateWorkflowDialogHost so shared
  // URLs that pre-date the global dialog still pop the create flow.
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
  // Track which workflow is currently having its state updated (enable/disable)
  const [updatingWorkflowName, setUpdatingWorkflowName] = useState<string | null>(null);
  // Local-only favorites (not persisted) — mirrors the Apps + Projects tables.
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set());
  const toggleFavorite = useCallback((name: string, next: boolean) => {
    setFavorites((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(name);
      else updated.delete(name);
      return updated;
    });
  }, []);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasWorkflows = useMemo(() => !!(workflows && workflows.length > 0), [workflows]);
  const { data: settings, isLoading: isSettingsLoading, isFetching: isSettingsFetching, isError: settingsInError, refetch: refetchSettings } = useEnvironmentSettings(projectName!, appName!, /* isDraftMode */ false);
  const { canEdit, isLoading: isPermissionsLoading } = useAppPermission();
  const updateSettingsMutation = useUpdateEnvironmentSettings(projectName!, appName!, /* isDraftMode */ false, /* prodSettings */ settings ?? []);

  useEffect(() => {
    if (searchParams.get(CREATE_FLAG_PARAM) !== '1') return;
    // Wait until permissions resolve before deciding. Otherwise the effect
    // fires once with canEdit=false (loading) and strips the flag, then
    // fires again with canEdit=true but the flag is already gone — leaving
    // the user staring at a list page with no dialog despite their click.
    if (isPermissionsLoading) return;
    if (canEdit) {
      openCreateWorkflowDialog({ source: 'deep-link' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete(CREATE_FLAG_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canEdit, isPermissionsLoading]);

  const deleteWorkflowMutation = useDeleteWorkflows(
    { appName: appName ?? '', projectName: projectName ?? '' },
    {
      onSuccess: async (workflowName) => {
        // Invalidate the exact key shape used by useAppResource / useAppWorkflows
        // so the list refreshes immediately after the delete.
        if (projectName && appName) {
          await queryClient.invalidateQueries({
            queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
          });
        }
        setWorkflowToDelete(null);
        addNotification({
          type: 'success',
          title: 'Workflow deleted',
          message: `Successfully deleted "${workflowName}".`,
        });
        // Note: useDeleteWorkflows already emits 'workflow.deleted' internally;
        // no duplicate trackEvent call here.
      },
      onError: (error) => {
        setWorkflowToDelete(null);
        addNotification({
          type: 'error',
          title: 'Failed to delete workflow',
          message: error.message ?? 'Unknown error',
        });
      },
    },
  );

  // Auto-sync onboarding: we're in an app so steps 1 & 2 are done
  useEffect(() => {
    if (!isLoading) {
      syncOnboardingFromData({ hasProjects: true, hasApps: true, hasWorkflows });
    }
  }, [hasWorkflows, isLoading]);

  const canBuildDesignerPath = useMemo(() => !!(locationName && projectName && appName), [locationName, projectName, appName]);

  const getDesignerPath = useCallback(
    (workflowName: string) => {
      if (canBuildDesignerPath) {
        return paths.appWorkflowDesigner(locationName!, projectName!, appName!, workflowName, tenantId);
      }
      return '#';
    },
    [locationName, projectName, appName, canBuildDesignerPath, tenantId],
  );

  // Hero handlers delegate to the global CreateWorkflowDialogHost (mounted
  // by AppLayout), which owns the mutation, error state, and navigation.
  const handleBuild = useCallback(
    (prompt: string) => {
      openCreateWorkflowDialog({ prompt, source: 'workflow-list-hero' });
    },
    [],
  );

  // Hero is gated on the project's createWorkflowFromPrompt capability.
  const isAssistantEnabled = useCreateWorkflowFromPromptEnabled();

  // Template gallery: open the gallery directly, let the user pick a template AND
  // enter a workflow name in the preview, then create+apply in one shot.
  const isTemplatesEnabled = useFeatureFlag('workflowTemplates');
  const [templateLibraryKey, setTemplateLibraryKey] = useState(0);
  const templateLibraryHeadingRef = useRef<HTMLHeadingElement>(null);
  const templateLibraryRetryRef = useRef<HTMLButtonElement>(null);
  const setTemplateLibraryRetryRef = useMergedRefs<HTMLButtonElement>(templateLibraryRetryRef);
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  const [templateSource, setTemplateSource] = useState<TemplateSource>('workflow-list-hero');

  const handleTemplateLibraryRetry = useCallback(() => {
    trackEvent('template.inline_retry', { source: 'error-boundary' });
    setTemplateLibraryKey((key) => key + 1);
  }, [trackEvent]);

  useEffect(() => {
    if (templateLibraryKey > 0) {
      (templateLibraryHeadingRef.current ?? templateLibraryRetryRef.current)?.focus();
    }
  }, [templateLibraryKey]);

  const handleBuildFromTemplate = useCallback((source: TemplateSource = 'workflow-list-hero') => {
    setTemplateSource(source);
    setIsTemplateGalleryOpen(true);
  }, []);

  const handleTemplateGalleryDismiss = useCallback(() => {
    setIsTemplateGalleryOpen(false);
  }, []);

  const defaultTemplateWorkflowName = useMemo(
    () => generateUniqueName(workflows ?? [], 'myWorkflow'),
    [workflows]
  );

  // The create call is an upsert (deployWorkflowArtifacts), so a duplicate name
  // would silently overwrite an existing workflow. Feed the current names into
  // the gallery's name field so duplicates are blocked client-side, matching
  // CreateWorkflowDialog's validation.
  const existingWorkflowNames = useMemo(
    () => (workflows ?? []).map((w) => extractWorkflowName(w.name)),
    [workflows]
  );

  // The template ID and source are captured at submit time and consumed by
  // onSuccess of the create-workflow mutation, so they must outlive the
  // async mutation (state may change before it resolves if the user opens
  // the gallery again from a different entry point).
  const pendingTemplateId = useRef<string | null>(null);
  const pendingTemplateSource = useRef<TemplateSource>('workflow-list-hero');

  const createFromTemplateMutation = useCreateWorkflow({
    appInfo: { projectName: projectName ?? '', appName: appName ?? '' },
    onSuccess: (workflowName) => {
      trackEvent('workflow.create.succeeded', {
        workflowName,
        source: pendingTemplateSource.current,
        flow: 'template-gallery',
      });
      if (projectName && appName) {
        queryClient.invalidateQueries({
          queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
        });
      }
      setIsTemplateGalleryOpen(false);
      if (canBuildDesignerPath && pendingTemplateId.current) {
        const designerPath = getDesignerPath(workflowName);
        navigate(`${designerPath}?applyTemplate=${encodeURIComponent(pendingTemplateId.current)}`);
        pendingTemplateId.current = null;
      }
    },
    onError: (err) => {
      addNotification({
        type: 'error',
        title: 'Failed to create workflow',
        message: err.message ?? 'Unknown error',
      });
    },
  });

  useEffect(() => {
    if (isTemplateGalleryOpen && templateSource === 'workflow-list-toolbar' &&
      (!canEdit || isPermissionsLoading || !isTemplatesEnabled) && !createFromTemplateMutation.isPending) {
      setIsTemplateGalleryOpen(false);
      refreshButtonRef.current?.focus();
      trackEvent('workflow.create.access_revoked', { source: templateSource });
    }
  }, [isTemplateGalleryOpen, templateSource, canEdit, isPermissionsLoading, isTemplatesEnabled, createFromTemplateMutation.isPending, trackEvent]);

  const handleCreateFromTemplate = useCallback(
    (templateId: string, workflowName: string, source: TemplateSource = templateSource) => {
      pendingTemplateId.current = templateId;
      pendingTemplateSource.current = source;
      trackEvent('workflow.create.started', {
        source: pendingTemplateSource.current,
        flow: 'template-gallery',
        templateId,
      });
      createFromTemplateMutation.mutate(workflowName);
    },
    [createFromTemplateMutation, templateSource, trackEvent]
  );

  const handleCreateFromInlineTemplate = useCallback(
    (templateId: string, workflowName: string) => {
      handleCreateFromTemplate(templateId, workflowName, 'workflow-list-empty-state');
    },
    [handleCreateFromTemplate],
  );

  const handleStartFromScratch = useCallback(() => {
    openCreateWorkflowDialog({ source: 'workflow-list-empty-state' });
  }, []);

  type WorkflowSortColumn = 'name' | 'state';
  const [sortColumn, setSortColumn] = useState<WorkflowSortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((column: WorkflowSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const filteredWorkflows = useMemo(() => {
    let result = workflows ?? [];
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter((w) => extractWorkflowName(w.name).toLowerCase().includes(query));
    }
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      switch (sortColumn) {
        case 'name':
          return dir * extractWorkflowName(a.name).localeCompare(extractWorkflowName(b.name));
        case 'state':
          return dir * (a.properties?.state ?? '').localeCompare(b.properties?.state ?? '');
        default:
          return 0;
      }
    });
  }, [workflows, search, sortColumn, sortDirection]);

  const pagination = usePagination<typeof filteredWorkflows[number]>({
    totalItems: filteredWorkflows.length,
    storageKey: 'otto.workflowList.pageSize',
    resetDeps: [search, sortColumn, sortDirection],
  });
  const pagedWorkflows = useMemo(
    () => pagination.paginate(filteredWorkflows),
    [pagination, filteredWorkflows],
  );
  const { data: draftStatuses } = useWorkflowDraftStatuses(
    workflows ?? [],
    projectName,
    appName,
    activeView === 'list',
  );

  const handleRefresh = useCallback(async (fetchSettings = true) => {
    // invalidateQueries (not resetQueries) keeps the previously-fetched data
    // visible while a background refetch runs. The Refresh button already
    // swaps to a spinner via `isFetching`, so the loading affordance is
    // preserved without unmounting the entire page back to the full skeleton.
    const projectKey = projectName?.toLowerCase();
    const appKey = appName?.toLowerCase();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['workflows', projectKey, appKey] }),
      queryClient.invalidateQueries({
        queryKey: workflowDraftStatusesQueryPrefix(projectName, appName),
      }),
      queryClient.invalidateQueries({
        queryKey: ['workflowDefinition', projectKey, appKey],
      }),
      queryClient.invalidateQueries({
        queryKey: ['workflowDesigner', projectKey, appKey],
      }),
    ]);
    await queryClient.invalidateQueries({
      queryKey: workflowGraphDefinitionQueryPrefix(projectName, appName),
    });
    if (fetchSettings) {
      await refetchSettings();
    }
  }, [queryClient, projectName, appName, refetchSettings]);

  const handleUpdateWorkflowState = useCallback(
    async (workflowName: string, disable: boolean) => {
      setUpdatingWorkflowName(extractWorkflowName(workflowName));
      try {
        const settingName = `Workflows.${extractWorkflowName(workflowName)}.FlowState`;
        const newValue = disable ? 'Disabled' : 'Enabled';
        const existingSettings = (settings ?? []).map((setting) => ({
          name: setting.name,
          value: setting.value ?? '',
        }));
        const existingIndex = existingSettings.findIndex(
          setting => setting.name.toLowerCase() === settingName.toLowerCase()
        );

        let patchedSettings: { name: string; value: string }[];
        if (existingIndex !== -1) {
          // Replace existing setting
          patchedSettings = existingSettings.map((setting, index) =>
            index === existingIndex ? { ...setting, value: newValue } : setting
          );
        } else {
          // Add new setting
          patchedSettings = [...existingSettings, { name: settingName, value: newValue }];
        }

        await updateSettingsMutation.mutateAsync({ properties: { settings: patchedSettings } });

        void handleRefresh(/* fetchSettings */ false);
        addNotification({
          type: 'success',
          title: `Workflow ${disable ? 'disabled' : 'enabled'}`,
          message: `Successfully ${disable ? 'disabled' : 'enabled'} "${extractWorkflowName(workflowName)}".`,
        });
        trackEvent('workflow.updateState.succeeded', {
          workflowName,
          newState: disable ? 'Disabled' : 'Enabled',
        });
      } catch (error: unknown) {
        setUpdatingWorkflowName(null);
        addNotification({
          type: 'error',
          title: `Failed to ${disable ? 'disable' : 'enable'} workflow`,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        trackEvent('workflow.updateState.failed', {
          workflowName,
          newState: disable ? 'Disabled' : 'Enabled',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setUpdatingWorkflowName(null);
      }
    },
    [settings, updateSettingsMutation, handleRefresh, addNotification, trackEvent],
  );

  const getWorkflowMenuItems = useCallback(
    (workflow: Workflow) =>
      createWorkflowActionMenuItems({
        workflow,
        canEdit,
        isPermissionsLoading,
        isStateActionDisabled:
          isSettingsLoading ||
          isSettingsFetching ||
          settingsInError ||
          updateSettingsMutation.isPending,
        onOpenDesigner: (workflowName) => {
          trackEvent('navigation.workflow.openDesigner', { workflowName });
          if (canBuildDesignerPath) {
            navigate(getDesignerPath(workflowName));
          }
        },
        onViewRuns: (workflowName) => {
          trackEvent('navigation.workflow.viewRuns', { workflowName });
          if (canBuildDesignerPath) {
            navigate(`${getDesignerPath(workflowName)}/runs`);
          }
        },
        onToggleState: (workflowName, disable) =>
          handleUpdateWorkflowState(workflowName, disable),
        onDelete: setWorkflowToDelete,
      }),
    [
      canBuildDesignerPath,
      canEdit,
      getDesignerPath,
      handleUpdateWorkflowState,
      isPermissionsLoading,
      isSettingsFetching,
      isSettingsLoading,
      navigate,
      settingsInError,
      trackEvent,
      updateSettingsMutation.isPending,
    ],
  );

  if (error) {
    return <ResourceErrorPage resourceType="workflow" error={error} onRetry={handleRefresh} />;
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        {appName && (
          <AppOverviewHeader
            name={appName}
            app={app}
            region={locationName}
            workflowCount={workflows?.length ?? 0}
          />
        )}
        <WorkflowListSkeleton isAssistantEnabled={isAssistantEnabled} />
      </div>
    );
  }

  return (
    <div
      className={mergeClasses(styles.page, !hasWorkflows && styles.emptyPage)}
      data-testid="workflow-list-page"
    >
      {appName && hasWorkflows && (
        <AppOverviewHeader
          name={appName}
          app={app}
          region={locationName}
          workflowCount={workflows?.length ?? 0}
        />
      )}
      {isAssistantEnabled && !hasWorkflows && (
        <WorkflowBuilderHero
          hasWorkflows={hasWorkflows}
          isCreating={false}
          error={null}
          disabled={!canEdit || isPermissionsLoading}
          onBuild={handleBuild}
        />
      )}

      {!hasWorkflows && canEdit && !isPermissionsLoading && (
        isTemplatesEnabled ? (
          <FeatureErrorBoundary
            key={templateLibraryKey}
            componentName="InlineTemplateGallery"
            fallback={
              <div className={styles.emptyLibraryError} role="alert">
                <Text>Template library couldn't be displayed.</Text>
                <div className={styles.emptyActionRow}>
                  <Button ref={setTemplateLibraryRetryRef} onClick={handleTemplateLibraryRetry}>Retry</Button>
                  <Button
                    appearance="outline"
                    icon={<AddRegular />}
                    onClick={handleStartFromScratch}
                  >
                    Start from scratch
                  </Button>
                </div>
              </div>
            }
          >
            <InlineTemplateGallery
              headingLevel={isAssistantEnabled ? 2 : 1}
              headingRef={templateLibraryHeadingRef}
              onStartFromScratch={handleStartFromScratch}
              onCreateFromTemplate={handleCreateFromInlineTemplate}
              defaultWorkflowName={defaultTemplateWorkflowName}
              existingWorkflowNames={existingWorkflowNames}
              isCreating={createFromTemplateMutation.isPending}
            />
          </FeatureErrorBoundary>
        ) : (
          <div className={styles.emptyActionRow}>
            <Button
              appearance="outline"
              icon={<AddRegular />}
              onClick={handleStartFromScratch}
            >
              Start from scratch
            </Button>
          </div>
        )
      )}

      {hasWorkflows && (
        <PageSection
          title="Workflows"
          className={styles.tableSection}
          {...(isGraphEnabled
            ? {
                titleAccessory: (
                  <TabList
                    className={styles.viewSwitcher}
                    size="small"
                    selectedValue={view}
                    aria-label={workflowListMessages.viewSwitcherLabel}
                    onTabSelect={(_event, data) => {
                      setWorkflowToDelete(null);
                      setView(data.value === 'graph' ? 'graph' : 'list');
                    }}
                  >
                    <Tab value="list">{workflowListMessages.listView}</Tab>
                    <Tab value="graph">{workflowListMessages.graphView}</Tab>
                  </TabList>
                ),
              }
            : {})}
        >
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <Input
                className={styles.search}
                size="small"
                contentBefore={<SearchRegular />}
                placeholder="Search workflows..."
                value={search}
                onChange={(_e, data) => setSearch(data.value)}
              />
            </div>
            <div className={styles.toolbarRight}>
              <Button
                ref={setRefreshButtonRef}
                appearance="subtle"
                size="small"
                icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
                onClick={() => void handleRefresh()}
                disabled={isFetching}
              >
                Refresh
              </Button>
              <WorkflowCreateMenu
                canCreate={canEdit && !isPermissionsLoading}
                isAssistantEnabled={isAssistantEnabled}
                isTemplatesEnabled={isTemplatesEnabled}
                onBuildFromTemplate={() => handleBuildFromTemplate('workflow-list-toolbar')}
                fallbackFocusRef={refreshButtonRef}
              />
            </div>
          </div>

          {filteredWorkflows.length === 0 ? (
            <EmptyState
              title="No workflows found"
              description="Try a different search term."
            />
          ) : activeView === 'graph' ? (
            <Suspense
              fallback={
                <div className={styles.graphLoading} role="status">
                  <Spinner label={workflowListMessages.graphLoading} />
                </div>
              }
            >
              <LazyWorkflowDependencyGraph
                workflows={filteredWorkflows}
                projectName={projectName}
                appName={appName}
                getWorkflowHref={getDesignerPath}
                getWorkflowMenuItems={getWorkflowMenuItems}
                onWorkflowNavigate={(workflowName) =>
                  trackEvent('navigation.workflow.selected', { workflowName })
                }
              />
            </Suspense>
          ) : (
            <DataTablePanel
              bodyTestId="workflow-table-scroll-area"
              flushFooter
              footer={
                <TablePaginationFooter
                  totalItems={filteredWorkflows.length}
                  currentPage={pagination.page}
                  pageSize={pagination.pageSize}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              }
            >
              <Table aria-label="Workflows table">
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.colName} aria-sort={sortColumn === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className={styles.sortableHeader} onClick={() => handleSort('name')}>
                  Name {sortColumn === 'name' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                </button>
              </TableHeaderCell>
              <TableHeaderCell className={styles.colDateModified}>Date modified</TableHeaderCell>
              <TableHeaderCell className={styles.colStatus} aria-sort={sortColumn === 'state' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className={styles.sortableHeader} onClick={() => handleSort('state')}>
                  Status {sortColumn === 'state' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                </button>
              </TableHeaderCell>
              <TableHeaderCell className={styles.actionsCell}>
                <span className={styles.srOnly}>Actions</span>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedWorkflows.map((workflow) => {
              const wfName = extractWorkflowName(workflow.name);
              const menuItems = getWorkflowMenuItems(workflow);

              return (
              <TableRow key={workflow.name}>
                <TableCell className={styles.colName}>
                  <EntityNameCell
                    icon={<Flow16Regular />}
                    name={wfName}
                    href={getDesignerPath(wfName)}
                    onClick={() => trackEvent('navigation.workflow.selected', { workflowName: wfName })}
                    isFavorite={favorites.has(wfName)}
                    onToggleFavorite={(next) => toggleFavorite(wfName, next)}
                  />
                </TableCell>
                <TableCell className={styles.colDateModified}>
                  <Text className={styles.metadata}>{formatDateTime(workflow.properties?.changedTime, 'full', { fallback: '—' })}</Text>
                </TableCell>
                <TableCell className={styles.colStatus}>
                  {updatingWorkflowName === wfName ? (
                    <Skeleton aria-label="Updating status">
                      <SkeletonItem size={16} style={{ width: '60px' }} />
                    </Skeleton>
                  ) : (
                    <div className={styles.statusBadges}>
                      <DotStatusBadge status={mapWorkflowState(workflow.properties?.state)} />
                      {draftStatuses?.byWorkflowId[workflow.id] && (
                        <WorkflowUnpublishedBadge />
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className={styles.actionsCell}>
                  <OverflowMenu items={menuItems} />
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
            </DataTablePanel>
          )}
        </PageSection>
      )}

      <DeleteConfirmDialog
        open={workflowToDelete !== null}
        onClose={() => {
          // Don't dismiss while the mutation is mid-flight.
          if (!deleteWorkflowMutation.isPending) {
            setWorkflowToDelete(null);
          }
        }}
        onConfirm={() => {
          if (workflowToDelete) {
            deleteWorkflowMutation.mutate(workflowToDelete);
          }
        }}
        itemType="workflow"
        itemNames={workflowToDelete ? [workflowToDelete] : []}
        isDeleting={deleteWorkflowMutation.isPending}
      />

      <TemplateGalleryDialog
        open={isTemplateGalleryOpen}
        onDismiss={handleTemplateGalleryDismiss}
        requireWorkflowName
        defaultWorkflowName={defaultTemplateWorkflowName}
        existingWorkflowNames={existingWorkflowNames}
        isCreating={createFromTemplateMutation.isPending}
        onCreateFromTemplate={handleCreateFromTemplate}
      />
    </div>
  );
};